import useBusinessPostApi from '../../lib/useBusinessPostApi.js';
import { setFlowQuery } from '../../lib/Queries';
import { useEffect, useState } from 'react';
import StepEdit from './StepEdit.jsx';
import Modal from '../Modal';
import { Actions } from './Actions.jsx';
import { minutesToDuration } from '../../lib/DateTime.js';
import {
    AdjustmentsHorizontalIcon,
    TrashIcon,
} from '@heroicons/react/24/outline/index.js';
import DotDotDotMenu from '../DotDotDotMenu';
import PencilIcon from '../icons/PencilIcon.jsx';
import CopyIcon from '../icons/CopyIcon.jsx';
import useDelete from '../../lib/useDelete.jsx';
import { NegativeOffsetTriggerTypes } from '~/lib/Flows.js';
import { deepCopy } from '~/lib/Utils.js';
import FlowConditionsPreview from '~/components/flows/FlowConditionsPreview.jsx';
import ErrorMessageBlock from '~/components/ErrorMessageBlock.jsx';
import { ClassToObjectTypes } from '~/lib/ObjectTypes.js';
import * as Toast from '~/lib/Toast';

const FlowStep = ({ step, flowableId, flowableType, schema, triggerTypes, isMarketingAutomation, className = '', onClick, onSaved, isDisabled }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [data, setData] = useState(deepCopy(step));

    const { saveDelete: doDelete, isDeleting } = useDelete(
        isMarketingAutomation ? `/marketing-automations/${step.id}/delete` : `/flows/${flowableType}/${flowableId}/${step.id}/delete`,
    );
    const { save, isSaving } = useBusinessPostApi(
        `/flows/${flowableType}/${flowableId}/${step.id}`,
    );

    const { save: saveNew, isSaving: isSavingNew } = useBusinessPostApi(
        isMarketingAutomation ? `/marketing-automations/0` : `/flows/${flowableType}/${flowableId}/0`,
    );

    useEffect(() => {
        setData(deepCopy(step));
    }, [JSON.stringify(step)]);

    function deleteStep(ev) {
        if (ev) ev.preventDefault();
        doDelete({}).then(result => {
            if (onSaved) onSaved();
            Toast.success('Successfully deleted');
            setFlowQuery(flowableType, flowableId, result);
        });
    }

    function edit(ev) {
        ev.preventDefault();

        if (onClick) {
            onClick();
        } else {
            setIsEditing(true);
        }
    }

    function duplicate(ev) {
        ev.preventDefault();

        saveNew({
            ...step,
        }).then(result => {
            if (onSaved) onSaved();
            setIsEditing(false);
            setFlowQuery(flowableType, flowableId, result);
            Toast.success('Successfully duplicated');
        });

        return true;
    }

    function doSubmit() {
        save({
            ...data,
        }).then(result => {
            if (onSaved) onSaved();
            setIsEditing(false);
            setFlowQuery(flowableType, flowableId, result);
        });
    }

    const actionConfig = Actions[step.action];

    const laterOrBefore = NegativeOffsetTriggerTypes.includes(step.trigger)
        ? 'before'
        : 'later';

    const condition = isMarketingAutomation ? step.target_conditions : step.condition;

    return (
        <div key={step.id} className={`panel p-4 mb-2 ${className}`}>
            <div className="flex">
                <div
                    className="w-5 h-5 text-primary-700 mt-1 mr-3 cursor-pointer"
                    onClick={edit}
                >
                    {actionConfig.icon}
                </div>
                <div className="flex flex-1 justify-between items-center">
                    <div className="flex-1 cursor-pointer" onClick={edit}>
                        <span className="font-semibold">
                            {step.offset > 0
                                ? `${minutesToDuration(step.offset)} ${laterOrBefore} `
                                : !isMarketingAutomation
                                  ? 'Immediately '
                                  : ''}
                        </span>
                        <span className={isDisabled ? 'text-gray-500' : ''}>
                            {` ${actionConfig.verb || 'Send'} ${actionConfig.name}`}

                            {isDisabled && (
                                <span className="tag bg-gray-500 ml-4">
                                    Disabled
                                </span>
                            )}
                        </span>
                        <div className="my-1 text-gray-500">
                            {actionConfig.preview(step.settings)}
                        </div>
                        {condition?.conditions?.length > 0 && (
                            <FlowConditionsPreview
                                conditional={condition}
                                schema={schema}
                            />
                        )}
                        {isMarketingAutomation &&
                            !condition?.conditions?.length && (
                                <ErrorMessageBlock>
                                    There are no conditions set for this
                                    automation so it will never be sent
                                </ErrorMessageBlock>
                            )}
                    </div>
                    <div>
                        <DotDotDotMenu
                            options={[
                                {
                                    name: 'Edit',
                                    onClick: edit,
                                    icon: <PencilIcon />,
                                },
                                {
                                    name: 'Duplicate',
                                    onClick: duplicate,
                                    icon: <CopyIcon />,
                                },
                                {
                                    name: isDeleting ? 'Deleting...' : 'Delete',
                                    onClick: deleteStep,
                                    isDelete: true,
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>
            {!onClick && (
                <Modal
                    widthClass="sm:max-w-[750px]"
                    isOpen={isEditing}
                    onClose={() => {
                        if (
                            JSON.stringify(step) === JSON.stringify(data) ||
                            confirm(
                                'You have unsaved changes, are you sure you want to close?',
                            )
                        ) {
                            setIsEditing(false);
                        }
                    }}
                >
                    <StepEdit
                        data={data}
                        setData={setData}
                        doSubmit={doSubmit}
                        isSubmitting={isSaving}
                        schema={schema}
                        triggerTypes={triggerTypes}
                        onDelete={deleteStep}
                        isDeleting={isDeleting}
                        isMarketingAutomation={isMarketingAutomation}
                    />
                </Modal>
            )}
        </div>
    );
};

export default FlowStep;
